import React, { createContext } from "react";
import { MediaItem, MediaItemUuid } from "@/types/media";
import { UploadLimitErrorType } from "@/constants/media";

export interface UploadErrorDetails {
    errorCode?: string;
    message: string;
    metadata?: Record<string, any>;
}

interface MediaState {
    // media items
    mediaItems: MediaItem[];
    pendingMediaItems: MediaItem[];
    completedMediaItems: MediaItem[];
    erroredMediaItems: MediaItem[];
    orderedMediaItems: MediaItem[];

    // storage upload dir
    storageUploadDir: string;

    // visited files tracking
    visitedFileIds: Set<string>;

    // stable text prompt proposals (generated once from sample of uploaded files)
    stableTextPromptProposals: string[] | undefined;

    // Video trimming
    mediaItemToTrimId?: MediaItemUuid;
    mediaItemToTrim?: MediaItem;
    currentTrimTimes: { startTime: number; endTime: number } | null;

    // Upload error handling
    uploadError?: UploadErrorDetails;
    uploadLimitError: UploadLimitErrorType | null;
}

export interface MediaActions {
    // media item management
    deleteMediaItem: (id: string, opts?: { keepInStorage: boolean }) => void;
    addMediaItems: (files: File[]) => Promise<MediaItem[]>;
    setMediaItemIds: React.Dispatch<React.SetStateAction<MediaItemUuid[]>>;

    // video trimming
    handleVideoTrim: (
        onComplete?: (trimmedFile: File) => void | Promise<void>
    ) => Promise<string | undefined>;
    handleVideoTrimCancel: () => void;
    rejectCurrentFile: () => void;

    // current media management
    setCurrentTrimTimes: React.Dispatch<
        React.SetStateAction<{ startTime: number; endTime: number } | null>
    >;

    // visited files management
    markFileAsVisited: (fileId: string) => void;
    isFileVisited: (fileId: string) => boolean;

    // upload error handling
    setUploadError: (error?: UploadErrorDetails) => void;
    clearUploadError: () => void;
    setUploadLimitError: (error: UploadLimitErrorType | null) => void;
    clearUploadLimitError: () => void;
}

export interface MediaContextType extends MediaState, MediaActions {}

export const MediaContext = createContext<MediaContextType | undefined>(undefined);

// === HOOKS ===
export function useMedia(): MediaContextType {
    const ctx = React.useContext(MediaContext);
    if (!ctx) throw new Error("useMedia must be used within a MediaProvider");
    return ctx;
}
